import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './AdmissionsForm.css';
import { API_BASE_URL, fetchWithCSRF } from '../config';
import SchoolAutocomplete from './SchoolAutocomplete';
import Footer from './Footer';
import FAQ from './FAQ';

// PrepScholar colors: Primary blue #2978BD, Dark blue #0a2540, Light blue #4a9eff
const MAJORS = [
  'Computer Science',
  'Engineering',
  'Business/Economics',
  'Biology/Pre-Med',
  'Psychology',
  'English/Literature',
  'Mathematics',
  'Physics',
  'Chemistry',
  'Political Science',
  'History',
  'Art/Design',
  'Music',
  'Theater/Drama',
  'Education',
  'Nursing',
  'Environmental Science',
  'Communications',
  'Philosophy',
  'Sociology',
  'Other'
];

function AdmissionsForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editReportUuid = searchParams.get('edit');

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 5;  // Now 5 steps: Basic Info, Academics, Major, Colleges, Save
  const [canSubmit, setCanSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  
  const [formData, setFormData] = useState({
    grade: '',
    gpa: '',
    testsTaken: [],  // Array of test types: 'psat89', 'psatnmsqt', 'sat', 'act'
    psat89Score: '',
    psatNmsqtScore: '',
    satMath: '',
    satReadingWriting: '',
    actMath: '',
    actEnglish: '',
    actReading: '',
    actScience: '',
    actWriting: '',
    selectedSchools: [],  // Array of school objects with {id, name, city, state}
    knowsMajor: 'yes',
    majors: [],
    answerer: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: ''
  });

  const [errors, setErrors] = useState({});

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Prevent auto-submission on step 4 by adding a small delay
    if (currentStep === totalSteps) {
      setCanSubmit(false);
      const timer = setTimeout(() => {
        setCanSubmit(true);
      }, 300); // 300ms delay before allowing submission
      return () => clearTimeout(timer);
    } else {
      setCanSubmit(false);
    }
  }, [currentStep, totalSteps]);

  // Fetch and pre-fill report data when in edit mode
  useEffect(() => {
    if (editReportUuid) {
      fetchAndPrefillReport(editReportUuid);
    }
  }, [editReportUuid]);

  const fetchAndPrefillReport = async (uuid) => {
    setIsLoadingReport(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/universities/reports/${uuid}/`);
      if (!response.ok) {
        console.error('Failed to fetch report for editing');
        return;
      }

      const report = await response.json();
      setIsEditMode(true);

      // Build tests taken array from individual scores
      const testsTaken = [];
      if (report.psat_89_score) testsTaken.push('psat89');
      if (report.psat_nmsqt_score) testsTaken.push('psatnmsqt');
      if (report.sat_math_score || report.sat_reading_writing_score) testsTaken.push('sat');
      if (report.act_math_score || report.act_english_score || report.act_reading_score) testsTaken.push('act');
      // Fallback to tests_taken if individual scores not available
      if (testsTaken.length === 0 && report.tests_taken) {
        testsTaken.push(...report.tests_taken);
      }

      // Parse schools_interested back to selectedSchools array
      // This is tricky because we stored as comma-separated names, not IDs
      // For now, we'll just display the names - user can modify if needed
      let selectedSchools = [];
      if (report.schools_interested) {
        const schoolNames = report.schools_interested.split(',').map(s => s.trim()).filter(Boolean);
        // Try to get school data from user_schools_analysis if available
        const userSchools = report.recommendations?.user_schools_analysis || [];
        selectedSchools = schoolNames.map(name => {
          const found = userSchools.find(s => s.name === name);
          if (found) {
            return { id: found.id, name: found.name, city: found.city, state: found.state };
          }
          return { id: null, name, city: '', state: '' };
        });
      }

      // Pre-fill form data
      setFormData({
        grade: report.grade || '',
        gpa: report.gpa ? String(report.gpa) : '',
        testsTaken: testsTaken,
        psat89Score: report.psat_89_score ? String(report.psat_89_score) : '',
        psatNmsqtScore: report.psat_nmsqt_score ? String(report.psat_nmsqt_score) : '',
        satMath: report.sat_math_score ? String(report.sat_math_score) : '',
        satReadingWriting: report.sat_reading_writing_score ? String(report.sat_reading_writing_score) : '',
        actMath: report.act_math_score ? String(report.act_math_score) : '',
        actEnglish: report.act_english_score ? String(report.act_english_score) : '',
        actReading: report.act_reading_score ? String(report.act_reading_score) : '',
        actScience: report.act_science_score ? String(report.act_science_score) : '',
        actWriting: report.act_writing_score ? String(report.act_writing_score) : '',
        selectedSchools: selectedSchools,
        knowsMajor: report.knows_major || 'yes',
        majors: report.majors || [],
        answerer: report.answerer || '',
        firstName: report.first_name || '',
        lastName: report.last_name || '',
        phone: report.phone || '',
        email: report.contact_email || ''
      });

    } catch (error) {
      console.error('Error fetching report for editing:', error);
    } finally {
      setIsLoadingReport(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleGpaChange = (e) => {
    let value = e.target.value;

    // Replace comma with decimal point
    value = value.replace(',', '.');

    // Only allow numbers and one decimal point
    if (!/^[0-9]*\.?[0-9]*$/.test(value)) {
      return;
    }

    // Limit to 2 decimal places
    const parts = value.split('.');
    if (parts[1] && parts[1].length > 2) {
      return;
    }

    // Limit total length (max 5 chars like "10.00" to allow typing before validation)
    if (value.length > 5) {
      return;
    }

    setFormData(prev => ({
      ...prev,
      gpa: value
    }));

    // Real-time validation
    const numValue = parseFloat(value);
    if (value && !isNaN(numValue)) {
      if (numValue > 4.0) {
        setErrors(prev => ({ ...prev, gpa: 'GPA cannot exceed 4.0 on an unweighted scale' }));
      } else if (numValue < 0) {
        setErrors(prev => ({ ...prev, gpa: 'GPA cannot be negative' }));
      } else {
        setErrors(prev => ({ ...prev, gpa: '' }));
      }
    } else if (!value) {
      setErrors(prev => ({ ...prev, gpa: '' }));
    }
  };

  const handleMajorToggle = (major) => {
    setFormData(prev => {
      const majors = prev.majors.includes(major)
        ? prev.majors.filter(m => m !== major)
        : prev.majors.length < 5
          ? [...prev.majors, major]
          : prev.majors;
      return { ...prev, majors };
    });

    // Clear error for majors field
    if (errors.majors) {
      setErrors(prev => ({ ...prev, majors: '' }));
    }
  };

  const handleTestToggle = (testType) => {
    setFormData(prev => {
      const testsTaken = prev.testsTaken.includes(testType)
        ? prev.testsTaken.filter(t => t !== testType)
        : [...prev.testsTaken, testType];
      return { ...prev, testsTaken };
    });
  };

  // Score validation ranges for each test type
  const scoreRanges = {
    psat89Score: { min: 240, max: 1440, name: 'PSAT 8/9' },
    psatNmsqtScore: { min: 320, max: 1520, name: 'PSAT/NMSQT' },
    satMath: { min: 200, max: 800, name: 'SAT Math' },
    satReadingWriting: { min: 200, max: 800, name: 'SAT Reading & Writing' },
    actMath: { min: 1, max: 36, name: 'ACT Math' },
    actEnglish: { min: 1, max: 36, name: 'ACT English' },
    actReading: { min: 1, max: 36, name: 'ACT Reading' },
    actScience: { min: 1, max: 36, name: 'ACT Science' },
    actWriting: { min: 1, max: 36, name: 'ACT Writing' }
  };

  const handleScoreChange = (e) => {
    const { name, value } = e.target;

    // Update the form data
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Real-time validation
    const range = scoreRanges[name];
    if (range && value) {
      const numValue = parseInt(value, 10);
      if (isNaN(numValue)) {
        setErrors(prev => ({ ...prev, [name]: `Please enter a valid number` }));
      } else if (numValue < range.min) {
        setErrors(prev => ({ ...prev, [name]: `${range.name} score cannot be less than ${range.min}` }));
      } else if (numValue > range.max) {
        setErrors(prev => ({ ...prev, [name]: `${range.name} score cannot exceed ${range.max}` }));
      } else {
        setErrors(prev => ({ ...prev, [name]: '' }));
      }
    } else if (!value) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.grade) newErrors.grade = 'Please select your grade level';
      if (!formData.answerer) newErrors.answerer = 'Please indicate who is filling out this form';
    }
    
    if (step === 2) {
      if (!formData.gpa) newErrors.gpa = 'Please enter your GPA';
      if (formData.gpa && (parseFloat(formData.gpa) < 0 || parseFloat(formData.gpa) > 4.0)) {
        newErrors.gpa = 'GPA must be between 0.0 and 4.0';
      }
      
      // Check that user selected at least one test
      if (formData.testsTaken.length === 0) {
        newErrors.testsTaken = 'Please select at least one test you have taken';
      }
      
      // Validate PSAT 8/9 if selected
      if (formData.testsTaken.includes('psat89')) {
        if (!formData.psat89Score || formData.psat89Score.trim() === '') {
          newErrors.psat89Score = 'Please enter your PSAT 8/9 score';
        } else if (parseInt(formData.psat89Score) < 240 || parseInt(formData.psat89Score) > 1440) {
          newErrors.psat89Score = 'PSAT 8/9 score must be between 240 and 1440';
        }
      }
      
      // Validate PSAT/NMSQT if selected
      if (formData.testsTaken.includes('psatnmsqt')) {
        if (!formData.psatNmsqtScore || formData.psatNmsqtScore.trim() === '') {
          newErrors.psatNmsqtScore = 'Please enter your PSAT/NMSQT score';
        } else if (parseInt(formData.psatNmsqtScore) < 320 || parseInt(formData.psatNmsqtScore) > 1520) {
          newErrors.psatNmsqtScore = 'PSAT/NMSQT score must be between 320 and 1520';
        }
      }
      
      // Validate SAT if selected
      if (formData.testsTaken.includes('sat')) {
        if (!formData.satMath || formData.satMath.trim() === '') {
          newErrors.satMath = 'Please enter your SAT Math score';
        } else if (parseInt(formData.satMath) < 200 || parseInt(formData.satMath) > 800) {
          newErrors.satMath = 'SAT Math must be between 200 and 800';
        }
        
        if (!formData.satReadingWriting || formData.satReadingWriting.trim() === '') {
          newErrors.satReadingWriting = 'Please enter your SAT Reading & Writing score';
        } else if (parseInt(formData.satReadingWriting) < 200 || parseInt(formData.satReadingWriting) > 800) {
          newErrors.satReadingWriting = 'SAT Reading & Writing must be between 200 and 800';
        }
      }
      
      // Validate ACT if selected
      if (formData.testsTaken.includes('act')) {
        if (!formData.actMath || formData.actMath.trim() === '') {
          newErrors.actMath = 'Please enter your ACT Math score';
        } else if (parseInt(formData.actMath) < 1 || parseInt(formData.actMath) > 36) {
          newErrors.actMath = 'ACT Math must be between 1 and 36';
        }
        
        if (!formData.actEnglish || formData.actEnglish.trim() === '') {
          newErrors.actEnglish = 'Please enter your ACT English score';
        } else if (parseInt(formData.actEnglish) < 1 || parseInt(formData.actEnglish) > 36) {
          newErrors.actEnglish = 'ACT English must be between 1 and 36';
        }
        
        if (!formData.actReading || formData.actReading.trim() === '') {
          newErrors.actReading = 'Please enter your ACT Reading score';
        } else if (parseInt(formData.actReading) < 1 || parseInt(formData.actReading) > 36) {
          newErrors.actReading = 'ACT Reading must be between 1 and 36';
        }
        
        // Validate optional ACT Science if provided
        if (formData.actScience && (parseInt(formData.actScience) < 1 || parseInt(formData.actScience) > 36)) {
          newErrors.actScience = 'ACT Science must be between 1 and 36';
        }
        
        // Validate optional ACT Writing if provided
        if (formData.actWriting && (parseInt(formData.actWriting) < 1 || parseInt(formData.actWriting) > 36)) {
          newErrors.actWriting = 'ACT Writing must be between 1 and 36';
        }
      }
    }

    // Step 3 validation
    if (step === 3) {
      if (!formData.knowsMajor) {
        newErrors.knowsMajor = 'Please select an option';
      } else if (formData.knowsMajor === 'yes' && formData.majors.length === 0) {
        newErrors.majors = 'Please select at least one major';
      }
    }

    // Step 5 validation (optional fields, but validate format if provided)
    if (step === 5) {
      if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      const nextStep = Math.min(currentStep + 1, totalSteps);
      setCurrentStep(nextStep);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setErrors({});
  };

  const saveAndNavigateToReport = async (payload, recommendations) => {
    const savePayload = {
      ...payload,
      recommendations: recommendations
    };

    const saveResponse = await fetchWithCSRF(`${API_BASE_URL}/api/universities/reports/save/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(savePayload)
    });

    if (!saveResponse.ok) {
      // Failed to save report
    }

    const savedReport = await saveResponse.json();

    // Track report generation in GA4
    if (window.gtag) {
      window.gtag('event', 'report_generated', {
        event_category: 'engagement',
        report_id: savedReport.uuid,
        grade: payload.grade,
        has_sat: !!payload.sat_score,
        has_act: !!payload.act_score,
        major_count: payload.majors?.length || 0
      });
    }

    // Navigate to report page with UUID
    navigate(`/report/${savedReport.uuid}`);
  };


  const submitForm = async (skipContact = false) => {
    // Only allow submission from step 5 and after delay
    if (currentStep !== totalSteps) {
      return;
    }

    if (!canSubmit) {
      return;
    }

    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare API request payload
      // Convert selectedSchools array to comma-separated names for backend
      const schoolsString = formData.selectedSchools.map(s => s.name).join(', ');
      // Send school IDs for precise DB lookup (avoids fuzzy name matching issues)
      const schoolIds = formData.selectedSchools
        .filter(s => s.id)
        .map(s => s.id);

      const payload = {
        gpa: parseFloat(formData.gpa),
        grade: formData.grade,
        state: formData.state || '',
        schools_interested: schoolsString,
        selected_school_ids: schoolIds,
        majors: formData.majors,
        knows_major: formData.knowsMajor,
        answerer: formData.answerer,
        contact_email: skipContact ? '' : (formData.email || ''),
        first_name: skipContact ? '' : (formData.firstName || ''),
        last_name: skipContact ? '' : (formData.lastName || ''),
        phone: skipContact ? '' : (formData.phone || '')
      };

      // Add tests taken array to track which tests the student actually took
      if (formData.testsTaken && formData.testsTaken.length > 0) {
        payload.tests_taken = formData.testsTaken;
      }

      // Add individual test scores for form pre-filling on future edits
      if (formData.psat89Score) {
        payload.psat_89_score = parseInt(formData.psat89Score);
      }
      if (formData.psatNmsqtScore) {
        payload.psat_nmsqt_score = parseInt(formData.psatNmsqtScore);
      }
      if (formData.satMath) {
        payload.sat_math_score = parseInt(formData.satMath);
      }
      if (formData.satReadingWriting) {
        payload.sat_reading_writing_score = parseInt(formData.satReadingWriting);
      }
      if (formData.actMath) {
        payload.act_math_score = parseInt(formData.actMath);
      }
      if (formData.actEnglish) {
        payload.act_english_score = parseInt(formData.actEnglish);
      }
      if (formData.actReading) {
        payload.act_reading_score = parseInt(formData.actReading);
      }
      if (formData.actScience) {
        payload.act_science_score = parseInt(formData.actScience);
      }
      if (formData.actWriting) {
        payload.act_writing_score = parseInt(formData.actWriting);
      }

      // Add composite test scores for matching algorithm
      // Prioritize PSAT/NMSQT over PSAT 8/9 for college matching (more relevant)
      if (formData.psatNmsqtScore) {
        payload.psat_score = parseInt(formData.psatNmsqtScore);
      } else if (formData.psat89Score) {
        payload.psat_score = parseInt(formData.psat89Score);
      }

      // Calculate total SAT score if sections provided
      if (formData.satMath && formData.satReadingWriting) {
        payload.sat_score = parseInt(formData.satMath) + parseInt(formData.satReadingWriting);
      }

      // Calculate composite ACT score if sections provided (average of math, english, reading, science)
      if (formData.actMath && formData.actEnglish && formData.actReading) {
        const actScores = [
          parseInt(formData.actMath),
          parseInt(formData.actEnglish),
          parseInt(formData.actReading)
        ];

        // Include science if provided
        if (formData.actScience) {
          actScores.push(parseInt(formData.actScience));
        }

        // Calculate composite (average, rounded)
        const actComposite = Math.round(actScores.reduce((a, b) => a + b, 0) / actScores.length);
        payload.act_score = actComposite;
      }

      // Handle edit mode vs new report
      if (isEditMode && editReportUuid) {
        // Update existing report
        const updateResponse = await fetchWithCSRF(`${API_BASE_URL}/api/universities/reports/${editReportUuid}/profile/`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload)
        });

        if (!updateResponse.ok) {
          const errorData = await updateResponse.json();
          throw new Error(errorData.error || 'Failed to update profile');
        }

        // Navigate back to the same report
        navigate(`/report/${editReportUuid}`);
      } else {
        // Create new report (existing flow)
        const response = await fetchWithCSRF(`${API_BASE_URL}/api/universities/recommendations/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to get recommendations');
        }

        const recommendations = await response.json();

        // Always save and navigate to report first - users can see full results
        await saveAndNavigateToReport(payload, recommendations);
      }
    } catch (error) {
      setIsSubmitting(false);
      alert(`Error: ${error.message}. Please make sure all required fields are filled.`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    submitForm(false);
  };

  const handleSkipContact = () => {
    submitForm(true);
  };

  const renderProgressBar = () => {
    const steps = [
      { num: 1, label: 'Basic Info' },
      { num: 2, label: 'Academics' },
      { num: 3, label: 'Major' },
      { num: 4, label: 'Colleges' },
      { num: 5, label: 'Save' }
    ];

    return (
      <div className="progress-container">
        <div className="progress-bar">
          {steps.map((step, index) => (
            <React.Fragment key={step.num}>
              <div className="progress-step-container">
                <div className={`progress-step ${currentStep >= step.num ? 'active' : ''} ${currentStep > step.num ? 'completed' : ''}`}>
                  {currentStep > step.num ? '✓' : step.num}
                </div>
                <span className={`step-label ${currentStep === step.num ? 'active-label' : ''}`}>
                  {step.label}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className={`progress-line ${currentStep > step.num ? 'completed' : ''}`}></div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };


  return (
    <div className="admissions-form-container">
      {/* New Narrow Header - matching report */}
      <div className="form-narrow-header">
        <div className="header-left">
          <img
            src={`${process.env.PUBLIC_URL}/PrepScholar-Logo-Colored.png`}
            alt="PrepScholar Logo" 
            className="header-logo-small"
          />
          <span className="app-name">My College Chances</span>
        </div>
        <div className="header-right">
          <a 
            href="tel:+18668115177" 
            className="header-cta"
          >
            Contact Us
          </a>
        </div>
      </div>

      {renderProgressBar()}

      {/* Loading Screen - for report fetching in edit mode */}
      {isLoadingReport && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner-large"></div>
            <h2>Loading your profile...</h2>
            <p>Please wait while we retrieve your saved information.</p>
          </div>
        </div>
      )}

      {/* Loading Screen - for form submission */}
      {isSubmitting && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner-large"></div>
            <h2>{isEditMode ? 'Updating Your Profile...' : 'Generating Your College Report...'}</h2>
            <p>This may take a few seconds. Please don't close this window.</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="admissions-form">
        {/* Step 1: Basic Information */}
        {currentStep === 1 && (
          <div className="step-content">
            <h2 className="step-title">{isEditMode ? 'Edit Your Profile' : "Let's start with the basics"}</h2>
            
            {/* Question: Who is answering */}
            <div className="form-section">
              <label className="form-label">
                Are you answering this for yourself or on behalf of your child? <span className="required">*</span>
              </label>
              {errors.answerer && <div className="error-message">{errors.answerer}</div>}
              <div className="radio-group">
                <label className="radio-option">
                  <input
                    type="radio"
                    name="answerer"
                    value="student"
                    checked={formData.answerer === 'student'}
                    onChange={handleInputChange}
                  />
                  <span>I'm a student answering for myself</span>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    name="answerer"
                    value="parent"
                    checked={formData.answerer === 'parent'}
                    onChange={handleInputChange}
                  />
                  <span>I'm a parent answering for my college-bound child</span>
                </label>
              </div>
            </div>

            {/* Question: Grade Level */}
            <div className="form-section">
              <label className="form-label">
                What grade are you (or is your student) in? <span className="required">*</span>
              </label>
              {errors.grade && <div className="error-message">{errors.grade}</div>}
              <div className="radio-group">
                <label className="radio-option">
                  <input
                    type="radio"
                    name="grade"
                    value="middle-school"
                    checked={formData.grade === 'middle-school'}
                    onChange={handleInputChange}
                  />
                  <span>Middle school (class of 2030+)</span>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    name="grade"
                    value="freshman"
                    checked={formData.grade === 'freshman'}
                    onChange={handleInputChange}
                  />
                  <span>Freshman (class of 2029)</span>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    name="grade"
                    value="sophomore"
                    checked={formData.grade === 'sophomore'}
                    onChange={handleInputChange}
                  />
                  <span>Sophomore (class of 2028)</span>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    name="grade"
                    value="junior"
                    checked={formData.grade === 'junior'}
                    onChange={handleInputChange}
                  />
                  <span>Junior (class of 2027)</span>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    name="grade"
                    value="senior"
                    checked={formData.grade === 'senior'}
                    onChange={handleInputChange}
                  />
                  <span>Senior (class of 2026)</span>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Academic Profile */}
        {currentStep === 2 && (
          <div className="step-content">
            <h2 className="step-title">Tell us about your academic performance</h2>
            
            {/* Question: GPA */}
            <div className="form-section">
              <label className="form-label" htmlFor="gpa">
                What's your GPA (out of 4.0)? <span className="required">*</span>
              </label>
              {errors.gpa && <div className="error-message">{errors.gpa}</div>}
              <input
                type="text"
                id="gpa"
                name="gpa"
                inputMode="decimal"
                placeholder="e.g., 3.75"
                value={formData.gpa}
                onChange={handleGpaChange}
                className="form-input gpa-input"
              />
              <p className="helper-text">Enter your unweighted GPA on a 4.0 scale</p>
            </div>

            {/* Question: Which tests have you taken */}
            <div className="form-section">
              <label className="form-label">
                Which standardized tests have you taken? <span className="required">*</span>
              </label>
              <p className="helper-text">Select all that apply. You can submit scores for multiple tests.</p>
              {errors.testsTaken && <div className="error-message">{errors.testsTaken}</div>}
              <div className="radio-group">
                <label className="radio-option">
                  <input
                    type="checkbox"
                    name="testsTaken"
                    value="psat89"
                    checked={formData.testsTaken.includes('psat89')}
                    onChange={() => handleTestToggle('psat89')}
                  />
                  <span>PSAT 8/9</span>
                </label>
                <label className="radio-option">
                  <input
                    type="checkbox"
                    name="testsTaken"
                    value="psatnmsqt"
                    checked={formData.testsTaken.includes('psatnmsqt')}
                    onChange={() => handleTestToggle('psatnmsqt')}
                  />
                  <span>PSAT/NMSQT</span>
                </label>
                <label className="radio-option">
                  <input
                    type="checkbox"
                    name="testsTaken"
                    value="sat"
                    checked={formData.testsTaken.includes('sat')}
                    onChange={() => handleTestToggle('sat')}
                  />
                  <span>SAT</span>
                </label>
                <label className="radio-option">
                  <input
                    type="checkbox"
                    name="testsTaken"
                    value="act"
                    checked={formData.testsTaken.includes('act')}
                    onChange={() => handleTestToggle('act')}
                  />
                  <span>ACT</span>
                </label>
              </div>
            </div>

            {/* Show PSAT 8/9 score fields if selected */}
            {formData.testsTaken.includes('psat89') && (
              <div className="form-section">
                <label className="form-label" htmlFor="psat89Score">
                  What's your PSAT 8/9 score? <span className="required">*</span>
                </label>
                <p className="helper-text">PSAT 8/9 is taken in 8th or 9th grade and ranges from 240 to 1440</p>
                {errors.psat89Score && <div className="error-message">{errors.psat89Score}</div>}
                <input
                  type="number"
                  id="psat89Score"
                  name="psat89Score"
                  min="240"
                  max="1440"
                  placeholder="e.g., 1200"
                  value={formData.psat89Score}
                  onChange={handleScoreChange}
                  className="form-input score-input"
                />
              </div>
            )}

            {/* Show PSAT/NMSQT score fields if selected */}
            {formData.testsTaken.includes('psatnmsqt') && (
              <div className="form-section">
                <label className="form-label" htmlFor="psatNmsqtScore">
                  What's your PSAT/NMSQT score? <span className="required">*</span>
                </label>
                <p className="helper-text">PSAT/NMSQT is taken in 10th or 11th grade and scores range from 320 to 1520</p>
                {errors.psatNmsqtScore && <div className="error-message">{errors.psatNmsqtScore}</div>}
                <input
                  type="number"
                  id="psatNmsqtScore"
                  name="psatNmsqtScore"
                  min="320"
                  max="1520"
                  placeholder="e.g., 1200"
                  value={formData.psatNmsqtScore}
                  onChange={handleScoreChange}
                  className="form-input score-input"
                />
              </div>
            )}

            {/* Show SAT score fields if SAT selected */}
            {formData.testsTaken.includes('sat') && (
              <div className="form-section">
                <label className="form-label">
                  What are your SAT section scores? <span className="required">*</span>
                </label>
                <p className="helper-text">Enter your scores for both sections</p>
                
                <div className="test-scores">
                  <div className="test-score-input">
                    <label htmlFor="satMath">SAT Math <span className="required">*</span></label>
                    {errors.satMath && <div className="error-message-inline">{errors.satMath}</div>}
                    <input
                      type="number"
                      id="satMath"
                      name="satMath"
                      min="200"
                      max="800"
                      placeholder="200-800"
                      value={formData.satMath}
                      onChange={handleScoreChange}
                      className="form-input score-input"
                    />
                  </div>
                  
                  <div className="test-score-input">
                    <label htmlFor="satReadingWriting">SAT Reading & Writing <span className="required">*</span></label>
                    {errors.satReadingWriting && <div className="error-message-inline">{errors.satReadingWriting}</div>}
                    <input
                      type="number"
                      id="satReadingWriting"
                      name="satReadingWriting"
                      min="200"
                      max="800"
                      placeholder="200-800"
                      value={formData.satReadingWriting}
                      onChange={handleScoreChange}
                      className="form-input score-input"
                    />
                  </div>
                  
                  {/* Display total SAT if both sections entered */}
                  {formData.satMath && formData.satReadingWriting && (
                    <div className="test-score-total">
                      <strong>Total SAT Score:</strong> {parseInt(formData.satMath) + parseInt(formData.satReadingWriting)}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Show ACT score fields if ACT selected */}
            {formData.testsTaken.includes('act') && (
              <div className="form-section">
                <label className="form-label">
                  What are your ACT section scores? <span className="required">*</span>
                </label>
                <p className="helper-text">Enter your scores for the required sections (Math, English, Reading). Science and Writing are optional.</p>
                
                <div className="test-scores">
                  <div className="test-score-input">
                    <label htmlFor="actMath">ACT Math <span className="required">*</span></label>
                    {errors.actMath && <div className="error-message-inline">{errors.actMath}</div>}
                    <input
                      type="number"
                      id="actMath"
                      name="actMath"
                      min="1"
                      max="36"
                      placeholder="1-36"
                      value={formData.actMath}
                      onChange={handleScoreChange}
                      className="form-input score-input"
                    />
                  </div>
                  
                  <div className="test-score-input">
                    <label htmlFor="actEnglish">ACT English <span className="required">*</span></label>
                    {errors.actEnglish && <div className="error-message-inline">{errors.actEnglish}</div>}
                    <input
                      type="number"
                      id="actEnglish"
                      name="actEnglish"
                      min="1"
                      max="36"
                      placeholder="1-36"
                      value={formData.actEnglish}
                      onChange={handleScoreChange}
                      className="form-input score-input"
                    />
                  </div>
                  
                  <div className="test-score-input">
                    <label htmlFor="actReading">ACT Reading <span className="required">*</span></label>
                    {errors.actReading && <div className="error-message-inline">{errors.actReading}</div>}
                    <input
                      type="number"
                      id="actReading"
                      name="actReading"
                      min="1"
                      max="36"
                      placeholder="1-36"
                      value={formData.actReading}
                      onChange={handleScoreChange}
                      className="form-input score-input"
                    />
                  </div>
                  
                  <div className="test-score-input">
                    <label htmlFor="actScience">ACT Science (optional)</label>
                    {errors.actScience && <div className="error-message-inline">{errors.actScience}</div>}
                    <input
                      type="number"
                      id="actScience"
                      name="actScience"
                      min="1"
                      max="36"
                      placeholder="1-36"
                      value={formData.actScience}
                      onChange={handleScoreChange}
                      className="form-input score-input"
                    />
                  </div>
                  
                  <div className="test-score-input">
                    <label htmlFor="actWriting">ACT Writing (optional)</label>
                    {errors.actWriting && <div className="error-message-inline">{errors.actWriting}</div>}
                    <input
                      type="number"
                      id="actWriting"
                      name="actWriting"
                      min="1"
                      max="36"
                      placeholder="1-36"
                      value={formData.actWriting}
                      onChange={handleScoreChange}
                      className="form-input score-input"
                    />
                  </div>
                  
                  {/* Display composite ACT if core sections entered */}
                  {formData.actMath && formData.actEnglish && formData.actReading && (
                    <div className="test-score-total">
                      <strong>ACT Composite:</strong> {
                        Math.round((
                          parseInt(formData.actMath) + 
                          parseInt(formData.actEnglish) + 
                          parseInt(formData.actReading) + 
                          (formData.actScience ? parseInt(formData.actScience) : 0)
                        ) / (formData.actScience ? 4 : 3))
                      }
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Show message if no test taken */}
            {formData.testsTaken.length === 0 && (
              <div className="form-section">
                <div className="info-message">
                  <p><strong>No test scores yet?</strong></p>
                  <p>We recommend taking at least the PSAT 8/9 or PSAT/NMSQT to get personalized college recommendations. Test scores help us match you with schools where you'll be competitive for admission.</p>
                  <p>Please select at least one test above and enter your scores to continue.</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Major Selection */}
        {currentStep === 3 && (
          <div className="step-content">
            <h2 className="step-title">What do you want to study?</h2>
            
            {/* Question: Major */}
            <div className="form-section">
              <label className="form-label">
                Do you know what you (your student) want(s) to study?
              </label>
              {errors.knowsMajor && <div className="error-message">{errors.knowsMajor}</div>}

              <div className="radio-group">
                <label className="radio-option">
                  <input
                    type="radio"
                    name="knowsMajor"
                    value="yes"
                    checked={formData.knowsMajor === 'yes'}
                    onChange={handleInputChange}
                  />
                  <span>Yes, I have specific majors in mind</span>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    name="knowsMajor"
                    value="no"
                    checked={formData.knowsMajor === 'no'}
                    onChange={handleInputChange}
                  />
                  <span>No, I'm still exploring career options</span>
                </label>
              </div>

              {formData.knowsMajor === 'yes' && (
                <div className="major-selection">
                  <p className="minor-label">Select up to 5 majors you're interested in:</p>
                  {errors.majors && <div className="error-message">{errors.majors}</div>}
                  <div className="major-chips">
                    {MAJORS.map((major, index) => (
                      <button
                        key={index}
                        type="button"
                        className={`major-chip ${formData.majors.includes(major) ? 'selected' : ''}`}
                        onClick={() => handleMajorToggle(major)}
                        disabled={!formData.majors.includes(major) && formData.majors.length >= 5}
                      >
                        {formData.majors.includes(major) && '✓ '}
                        {major}
                      </button>
                    ))}
                  </div>
                  {formData.majors.length > 0 && (
                    <p className="selected-count">{formData.majors.length} of 5 majors selected</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 4: College Interests */}
        {currentStep === 4 && (
          <div className="step-content">
            <h2 className="step-title">What colleges are you considering?</h2>

            {/* Question: School List */}
            <div className="form-section">
              <label className="form-label">
                What schools are already on your list?
              </label>
              <p className="helper-text">Start typing to search for colleges. After your first selection, we'll recommend schools that match your profile.</p>

              <SchoolAutocomplete
                selectedSchools={formData.selectedSchools}
                onSchoolsChange={(schools) => setFormData(prev => ({ ...prev, selectedSchools: schools }))}
                majors={formData.majors}
                studentProfile={{
                  gpa: formData.gpa ? parseFloat(formData.gpa) : null,
                  sat_score: (formData.satMath && formData.satReadingWriting)
                    ? parseInt(formData.satMath) + parseInt(formData.satReadingWriting)
                    : null,
                  act_score: (formData.actMath && formData.actEnglish && formData.actReading)
                    ? Math.round(
                        (parseInt(formData.actMath) + parseInt(formData.actEnglish) + parseInt(formData.actReading) + (formData.actScience ? parseInt(formData.actScience) : 0))
                        / (formData.actScience ? 4 : 3)
                      )
                    : null,
                  psat_score: formData.psatNmsqtScore
                    ? parseInt(formData.psatNmsqtScore)
                    : (formData.psat89Score ? parseInt(formData.psat89Score) : null),
                  grade: formData.grade || null,
                  state: formData.state || null,
                  majors: formData.majors || [],
                  knows_major: formData.knowsMajor || 'no',
                }}
              />
            </div>
          </div>
        )}

        {/* Step 5: Contact Information (New Design) */}
        {currentStep === 5 && (
          <div className="step-content step-5-container">
            <h2 className="step-title-center">Where Should We Send Your Personalized College Report?</h2>
            
            <div className="step-5-layout">
              {/* Left Column: Features */}
              <div className="features-box">
                <div className="features-header">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 6L12 13L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Unlock these exclusive features by providing your email:</span>
                </div>
                
                <ul className="features-list">
                  <li>
                    <div className="feature-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#2978BD" strokeWidth="2"/>
                        <path d="M12 6V12L16 14" stroke="#2978BD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div>
                      <strong>Permanent Access:</strong> Return to anytime with magic link.
                    </div>
                  </li>
                  <li>
                    <div className="feature-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22 10V15C22 20 18 22 12 22C6 22 2 20 2 15V10C2 5 6 2 12 2C18 2 22 5 22 10Z" stroke="#2978BD" strokeWidth="2"/>
                        <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" stroke="#2978BD" strokeWidth="2"/>
                      </svg>
                    </div>
                    <div>
                      <strong>Admissions Insights:</strong> Get a custom guide for a major and target schools.
                    </div>
                  </li>
                </ul>
              </div>

              {/* Right Column: Form */}
              <div className="contact-form-section">
                <h3 className="contact-form-title">Secure Your Results</h3>
                
                <div className="form-group">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    className="form-input"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="John"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    className="form-input"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Doe"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  {errors.email && <div className="error-message-inline">{errors.email}</div>}
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="form-input"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your.email@example.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone Number (Optional)</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className="form-input"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div className="step-5-actions">
                  <button 
                    type="submit" 
                    className="secure-results-btn"
                    disabled={!canSubmit}
                  >
                    {canSubmit ? (isEditMode ? 'Update Profile & View →' : 'Secure My Results & View →') : 'Loading...'}
                  </button>
                  
                  <button 
                    type="button" 
                    className="no-thanks-link"
                    onClick={handleSkipContact}
                  >
                    No thanks, just show my results in the browser
                  </button>
                </div>

                <div className="security-note">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" fill="#2978BD" stroke="#2978BD" strokeWidth="2"/>
                    <path d="M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11" stroke="#2978BD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Your data is secure. We never sell your contact information to third parties.</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="form-navigation">
          {currentStep > 1 && (
            <button type="button" onClick={handlePrevious} className="nav-button prev-button">
              ← Previous
            </button>
          )}
          
          <div className="step-indicator-mobile">
            Step {currentStep} of {totalSteps}
          </div>
          
          {currentStep < totalSteps ? (
            <button type="button" onClick={handleNext} className="nav-button next-button">
              Next →
            </button>
          ) : (
            <button 
              type="submit" 
              className="nav-button submit-button"
              disabled={!canSubmit}
            >
              {canSubmit ? (isEditMode ? 'Update Profile →' : 'Get My Report →') : 'Loading...'}
            </button>
          )}
        </div>
      </form>

      {currentStep === 1 && <FAQ />}

      <Footer showDebugToggle={false} />
    </div>
  );
}

export default AdmissionsForm;

